import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuthFetch } from '../hooks/useAuthFetch';
import { useUsage } from '../hooks/useUsage';
import './Pages.css';

interface ApiKey {
  id: string;
  prefix: string;
  label: string;
  createdAt: string;
}

type DevTab = 'keys' | 'rest' | 'mcp';

const TAB_ITEMS: { id: DevTab; label: string; icon: string }[] = [
  { id: 'keys', label: 'Keys & Usage', icon: '🔑' },
  { id: 'rest', label: 'REST API', icon: '📡' },
  { id: 'mcp', label: 'MCP', icon: '🤖' },
];

export const DeveloperPage: React.FC = () => {
  const authFetch = useAuthFetch();
  const { usage, loading: usageLoading, error: usageError, refreshUsage } = useUsage();
  const [activeTab, setActiveTab] = useState<DevTab>('keys');

  // API keys state
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [keysLoading, setKeysLoading] = useState(true);
  const [keysError, setKeysError] = useState<string | null>(null);
  const [newKeyLabel, setNewKeyLabel] = useState('');
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedSnippet, setCopiedSnippet] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Ref for scrolling to key banner
  const keyBannerRef = useRef<HTMLDivElement>(null);

  // Derived: user already has a key
  const hasKey = keys.length > 0;

  // Fetch existing keys
  const fetchKeys = useCallback(async () => {
    setKeysLoading(true);
    setKeysError(null);
    try {
      const res = await authFetch('/api/keys');
      if (res.ok) {
        const data = await res.json();
        setKeys(data.keys || []);
      } else {
        const data = await res.json().catch(() => null);
        setKeysError(data?.detail || `Failed to load keys (${res.status})`);
      }
    } catch (err: any) {
      setKeysError(err.message || 'Failed to load API keys');
    } finally {
      setKeysLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  // Create key
  const handleCreateKey = async () => {
    setCreating(true);
    setError(null);
    setCreatedKey(null);
    try {
      const res = await authFetch('/api/keys', {
        method: 'POST',
        body: JSON.stringify({ label: newKeyLabel || 'My API Key' }),
      });
      if (res.ok) {
        const data = await res.json();
        setCreatedKey(data.rawKey);
        setNewKeyLabel('');
        fetchKeys();
        refreshUsage();
        setTimeout(() => {
          keyBannerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 150);
      } else {
        const data = await res.json().catch(() => null);
        setError(data?.detail || `Failed to create API key (${res.status})`);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  // Revoke key
  const handleRevokeKey = async (keyId: string) => {
    if (!window.confirm('Revoke this API key? Any applications using it will stop working.')) return;
    setRevoking(keyId);
    setError(null);
    try {
      const res = await authFetch(`/api/keys/${keyId}`, { method: 'DELETE' });
      if (res.ok) {
        setCreatedKey(null);
        fetchKeys();
      } else {
        const data = await res.json().catch(() => null);
        setError(data?.detail || 'Failed to revoke key');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to revoke key');
    } finally {
      setRevoking(null);
    }
  };

  // Copy key to clipboard
  const handleCopyKey = () => {
    if (createdKey) {
      navigator.clipboard.writeText(createdKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleCopySnippet = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedSnippet(id);
    setTimeout(() => setCopiedSnippet(null), 2000);
  };

  const CodeBlock: React.FC<{ id: string; code: string }> = ({ id, code }) => (
    <div className="dev-code-block-wrapper">
      <button
        className={`dev-code-copy-btn${copiedSnippet === id ? ' copied' : ''}`}
        onClick={() => handleCopySnippet(code, id)}
        title="Copy to clipboard"
      >
        {copiedSnippet === id ? '✓ Copied' : '📋 Copy'}
      </button>
      <pre className="dev-code-block">{code}</pre>
    </div>
  );

  // Usage bar helper
  const UsageBar: React.FC<{ used: number; limit: number; label: string; icon: string }> = ({ used, limit, label, icon }) => {
    const pct = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
    const color = pct >= 90 ? 'var(--color-error, #ef4444)' : pct >= 70 ? 'var(--color-warning, #f59e0b)' : 'var(--color-success, #22c55e)';
    return (
      <div className="dev-usage-bar-row">
        <div className="dev-usage-bar-label">
          <span>{icon} {label}</span>
          <span className="dev-usage-bar-count">{used} / {limit}</span>
        </div>
        <div className="dev-usage-bar-track">
          <div className="dev-usage-bar-fill" style={{ width: `${pct}%`, background: color }} />
        </div>
      </div>
    );
  };

  const maskedKey = (prefix: string) => `${prefix}${'•'.repeat(40)}`;
  const sampleKey = createdKey || (keys.length > 0 ? maskedKey(keys[0].prefix) : 'arak_live_YOUR_KEY_HERE');

  // ── Tab: Keys & Usage ──
  const renderKeysTab = () => (
    <>
      {/* API Keys */}
      <section className="dev-section dev-section-first">
        <h2 className="dev-section-title">🔑 API Key</h2>
        <p className="dev-section-desc">
          Your API key lets you authenticate programmatically. You get <strong>one key</strong> — the raw value is shown only at creation, so copy it right away.
        </p>

        {!hasKey && !createdKey && (
          <div className="dev-key-create-row">
            <input
              type="text"
              placeholder="Key label (e.g. CI Pipeline)"
              value={newKeyLabel}
              onChange={(e) => setNewKeyLabel(e.target.value)}
              className="dev-key-input"
              maxLength={64}
            />
            <button onClick={handleCreateKey} disabled={creating || keysLoading} className="dev-key-create-btn">
              {creating ? 'Creating…' : '+ Create Key'}
            </button>
          </div>
        )}

        {createdKey && (
          <div ref={keyBannerRef} className="dev-key-created-banner fade-in-up">
            <div className="dev-key-created-header">
              <span>✅ Key created — copy it now, it won't be shown again!</span>
            </div>
            <div className="dev-key-created-value">
              <code>{createdKey}</code>
              <button onClick={handleCopyKey} className="dev-copy-btn">
                {copied ? '✓ Copied' : '📋 Copy'}
              </button>
            </div>
          </div>
        )}

        {keysLoading ? (
          <p className="dev-muted">Loading keys…</p>
        ) : keysError ? (
          <div className="dev-error-banner">
            <span>⚠️ {keysError}</span>
            <button onClick={() => { setKeysError(null); fetchKeys(); }} className="dev-error-dismiss" title="Retry">↻</button>
          </div>
        ) : keys.length === 0 && !createdKey ? (
          <p className="dev-muted">No API key yet. Create one above to get started.</p>
        ) : keys.length > 0 ? (
          <div className="dev-keys-table-wrap">
            <table className="dev-keys-table">
              <thead>
                <tr>
                  <th>Label</th>
                  <th>Key Prefix</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {keys.map((k) => (
                  <tr key={k.id}>
                    <td className="dev-key-label-cell">{k.label || '—'}</td>
                    <td><code className="dev-key-prefix">{k.prefix}••••</code></td>
                    <td className="dev-key-date">{new Date(k.createdAt).toLocaleDateString()}</td>
                    <td>
                      <button
                        className="dev-revoke-btn"
                        onClick={() => handleRevokeKey(k.id)}
                        disabled={revoking === k.id}
                      >
                        {revoking === k.id ? 'Revoking…' : 'Revoke'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="dev-muted" style={{ marginTop: 8 }}>To create a new key, revoke the existing one first.</p>
          </div>
        ) : null}
      </section>

      {/* Usage */}
      <section className="dev-section">
        <h2 className="dev-section-title">📊 Usage</h2>
        {usageLoading ? (
          <p className="dev-muted">Loading usage…</p>
        ) : usageError ? (
          <div className="dev-error-banner">
            <span>⚠️ {usageError}</span>
            <button onClick={() => refreshUsage()} className="dev-error-dismiss" title="Retry">↻</button>
          </div>
        ) : !usage ? (
          <p className="dev-muted">Usage data unavailable.</p>
        ) : (
          <div className="dev-usage-grid">
            <UsageBar icon="📤" label="Resume Uploads" used={usage.usage.resumeUploads.used} limit={usage.usage.resumeUploads.monthly} />
            <UsageBar icon="💾" label="Stored Resumes" used={usage.usage.resumeUploads.stored} limit={usage.usage.resumeUploads.maxStored} />
            <div className="dev-usage-bar-row">
              <div className="dev-usage-bar-label">
                <span>📁 Max File Size</span>
                <span className="dev-usage-bar-count">{formatBytes(usage.fileSize.maxBytes)}</span>
              </div>
            </div>
            <div className="dev-usage-plan-badge">
              Plan: <strong>{usage.planDisplayName}</strong>
            </div>
          </div>
        )}
      </section>
    </>
  );

  // ── Tab: REST API ──
  const renderRestTab = () => (
    <>
      {/* API Docs link */}
      <section className="dev-section dev-section-first">
        <h2 className="dev-section-title">📖 Interactive Documentation</h2>
        <p className="dev-section-desc">
          Explore all public endpoints, request/response schemas, and try them live. Powered by the OpenAPI 3.1 specification.
        </p>
        <a
          href="https://rankify-api.aramailabs.com/api/docs"
          target="_blank"
          rel="noopener noreferrer"
          className="dev-key-create-btn"
          style={{ display: 'inline-block', textDecoration: 'none', textAlign: 'center', marginBottom: 8 }}
        >
          Open API Documentation ↗
        </a>
      </section>

      {/* Quick Start */}
      <section className="dev-section">
        <h2 className="dev-section-title">🚀 Quick Start</h2>
        <p className="dev-section-desc">
          Match resumes against a job description using semantic search + AI re-ranking.
          All endpoints accept the <code>X-API-Key</code> header. Rate-limit info is returned in response headers.
        </p>

        <h3 className="dev-snippet-lang">cURL</h3>
        <CodeBlock id="curl" code={`# Match uploaded resumes against a job description
curl -X POST \\
  https://rankify-api.aramailabs.com/api/match \\
  -H "X-API-Key: ${sampleKey}" \\
  -H "Content-Type: application/json" \\
  -d '{"text": "Senior Full-Stack Engineer with 5+ years experience in React, Node.js, and cloud platforms...", "top_n": 5}'`} />

        <h3 className="dev-snippet-lang">Python</h3>
        <CodeBlock id="python" code={`import requests

API_KEY = "${sampleKey}"
BASE = "https://rankify-api.aramailabs.com"

# Match resumes against a job description
resp = requests.post(
    f"{BASE}/api/match",
    headers={"X-API-Key": API_KEY, "Content-Type": "application/json"},
    json={
        "text": "Senior Full-Stack Engineer with 5+ years experience in React, Node.js...",
        "top_n": 5,
    },
)
result = resp.json()
for r in result["rankings"]:
    print(f'{r["rank"]}. {r["resumeFilename"]} — {r["score"]}%')
    print(f'   Strengths:    {", ".join(r["strengths"][:3])}')
    print(f'   Improvements: {", ".join(r["improvements"][:3])}')`} />

        <h3 className="dev-snippet-lang">JavaScript (fetch)</h3>
        <CodeBlock id="js" code={`const API_KEY = "${sampleKey}";
const BASE = "https://rankify-api.aramailabs.com";

const res = await fetch(\`\${BASE}/api/match\`, {
  method: "POST",
  headers: {
    "X-API-Key": API_KEY,
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    text: "Senior Full-Stack Engineer with 5+ years...",
    top_n: 5,
  }),
});
const { rankings, summary } = await res.json();
rankings.forEach(r =>
  console.log(\`#\${r.rank} \${r.resumeFilename}: \${r.score}%\`)
);`} />

        <h3 className="dev-snippet-lang">Sample Response</h3>
        <CodeBlock id="response" code={`{
  "rankings": [
    {
      "rank": 1,
      "resumeFilename": "alice_resume.pdf",
      "score": 87,
      "strengths": ["5 yrs React/Node", "AWS certified", "Led team of 8"],
      "improvements": ["No GraphQL experience", "Missing CI/CD mentions"],
      "explanation": "Strong full-stack match with proven leadership..."
    },
    { "rank": 2, "resumeFilename": "bob_resume.pdf", "score": 72, ... }
  ],
  "summary": "Alice is the strongest match with 87% alignment...",
  "metadata": { "totalCandidates": 2, "avgScore": 79.5, "topCandidate": "alice_resume.pdf" },
  "processingTime": 3400
}`} />

        <h3 className="dev-snippet-lang">Response Headers</h3>
        <CodeBlock id="headers" code={`X-RateLimit-Plan: free
X-RateLimit-Limit: 50        # monthly ranking quota
X-RateLimit-Remaining: 42    # rankings left this period
X-RateLimit-Reset: 2026-04-01T00:00:00Z`} />
      </section>
    </>
  );

  // ── Tab: MCP ──
  const renderMcpTab = () => (
    <>
      {/* Overview */}
      <section className="dev-section dev-section-first">
        <h2 className="dev-section-title">🤖 Model Context Protocol (MCP)</h2>
        <p className="dev-section-desc">
          Connect xRank to AI coding assistants and agents via the <strong>Model Context Protocol</strong>.
          Your AI assistant gets direct access to resume management — upload, search, rank, and manage job descriptions — all through natural language.
        </p>

        <div className="dev-mcp-endpoint-card">
          <div className="dev-mcp-endpoint-row">
            <span className="dev-mcp-endpoint-label">Endpoint</span>
            <code className="dev-mcp-endpoint-value">https://rankify-api.aramailabs.com/mcp</code>
          </div>
          <div className="dev-mcp-endpoint-row">
            <span className="dev-mcp-endpoint-label">Transport</span>
            <code className="dev-mcp-endpoint-value">Streamable HTTP</code>
          </div>
          <div className="dev-mcp-endpoint-row">
            <span className="dev-mcp-endpoint-label">Auth</span>
            <code className="dev-mcp-endpoint-value">X-API-Key header</code>
          </div>
        </div>
      </section>

      {/* Available Tools */}
      <section className="dev-section">
        <h2 className="dev-section-title">🛠 Available Tools</h2>
        <p className="dev-section-desc">
          Your AI assistant can call these 28 tools after connecting. Each tool respects your plan's quota limits.
        </p>
        <div className="dev-mcp-tools-grid">
          <div className="dev-mcp-tool-card">
            <strong>check_duplicates</strong>
            <span>Check if files already exist in the talent pool before uploading</span>
          </div>
          <div className="dev-mcp-tool-card">
            <strong>upload_resume</strong>
            <span>Upload a PDF/DOCX resume from a URL or base64 content</span>
          </div>
          <div className="dev-mcp-tool-card">
            <strong>list_resumes</strong>
            <span>List all resumes in your library with pagination</span>
          </div>
          <div className="dev-mcp-tool-card">
            <strong>delete_resume</strong>
            <span>Delete a resume by ID (soft or permanent)</span>
          </div>
          <div className="dev-mcp-tool-card">
            <strong>bulk_delete_resumes</strong>
            <span>Delete multiple resumes in a single request</span>
          </div>
          <div className="dev-mcp-tool-card">
            <strong>preview_resume</strong>
            <span>Get a signed URL to preview or download a resume</span>
          </div>
          <div className="dev-mcp-tool-card">
            <strong>tag_resume</strong>
            <span>Set tags on a resume for filtering and organization</span>
          </div>
          <div className="dev-mcp-tool-card">
            <strong>get_enrichment_details</strong>
            <span>Get enrichment metadata — skills, bias flags, confidence</span>
          </div>
          <div className="dev-mcp-tool-card">
            <strong>save_job_description</strong>
            <span>Save a job description from text or URL</span>
          </div>
          <div className="dev-mcp-tool-card">
            <strong>list_job_descriptions</strong>
            <span>List saved job descriptions with pagination</span>
          </div>
          <div className="dev-mcp-tool-card">
            <strong>get_job_description</strong>
            <span>Get full text of a specific job description</span>
          </div>
          <div className="dev-mcp-tool-card">
            <strong>delete_job_description</strong>
            <span>Delete a job description by ID</span>
          </div>
          <div className="dev-mcp-tool-card">
            <strong>update_job_description</strong>
            <span>Rename a saved job description</span>
          </div>
          <div className="dev-mcp-tool-card">
            <strong>update_job_status</strong>
            <span>Set job status to open, filled, or closed</span>
          </div>
          <div className="dev-mcp-tool-card">
            <strong>tag_job_description</strong>
            <span>Set tags on a job description for organization</span>
          </div>
          <div className="dev-mcp-tool-card">
            <strong>link_resume_to_job</strong>
            <span>Add a resume to a job's candidate pool</span>
          </div>
          <div className="dev-mcp-tool-card">
            <strong>unlink_resume_from_job</strong>
            <span>Remove a resume from a job's candidate pool</span>
          </div>
          <div className="dev-mcp-tool-card">
            <strong>bulk_link_resumes_to_job</strong>
            <span>Link multiple resumes to a job at once</span>
          </div>
          <div className="dev-mcp-tool-card">
            <strong>list_role_candidates</strong>
            <span>List all resumes linked to a specific job</span>
          </div>
          <div className="dev-mcp-tool-card">
            <strong>match_resumes</strong>
            <span>Rank resumes against a JD using semantic search + AI</span>
          </div>
          <div className="dev-mcp-tool-card">
            <strong>get_match_result</strong>
            <span>Retrieve a match result with full ranking details</span>
          </div>
          <div className="dev-mcp-tool-card">
            <strong>list_match_results</strong>
            <span>List match result history, optionally filtered by JD</span>
          </div>
          <div className="dev-mcp-tool-card">
            <strong>get_usage</strong>
            <span>Check current plan limits and usage stats</span>
          </div>
          <div className="dev-mcp-tool-card">
            <strong>get_my_org</strong>
            <span>Get organization details, plan, members, and settings</span>
          </div>
          <div className="dev-mcp-tool-card">
            <strong>generate_report_pdf</strong>
            <span>Generate a downloadable PDF report from normalized report payloads</span>
          </div>
          <div className="dev-mcp-tool-card">
            <strong>clone_job_description</strong>
            <span>Clone an existing job description into a new open role</span>
          </div>
          <div className="dev-mcp-tool-card">
            <strong>release_candidate</strong>
            <span>Move a placed candidate back to the active talent pool</span>
          </div>
          <div className="dev-mcp-tool-card">
            <strong>release_candidates</strong>
            <span>Bulk release multiple placed candidates back to the active pool</span>
          </div>
        </div>
      </section>

      {/* Client Configs */}
      <section className="dev-section">
        <h2 className="dev-section-title">⚙️ Client Configuration</h2>
        <p className="dev-section-desc">
          Copy the JSON config for your AI client. Replace the API key placeholder if it's not auto-filled — get your key from the <button className="dev-tab-link" onClick={() => setActiveTab('keys')}>Keys &amp; Usage</button> tab.
        </p>

        <h3 className="dev-snippet-lang">VS Code / GitHub Copilot</h3>
        <p className="dev-muted" style={{ fontStyle: 'normal', marginBottom: 8 }}>
          Add to <code>.vscode/mcp.json</code> in your project root:
        </p>
        <CodeBlock id="mcp-vscode" code={`{
  "servers": {
    "xrank": {
      "url": "https://rankify-api.aramailabs.com/mcp",
      "type": "http",
      "headers": {
        "X-API-Key": "${sampleKey}"
      }
    }
  }
}`} />

        <h3 className="dev-snippet-lang">Claude Desktop</h3>
        <p className="dev-muted" style={{ fontStyle: 'normal', marginBottom: 8 }}>
          Add to <code>claude_desktop_config.json</code>:
        </p>
        <CodeBlock id="mcp-claude" code={`{
  "mcpServers": {
    "xrank": {
      "url": "https://rankify-api.aramailabs.com/mcp",
      "transport": "streamable-http",
      "headers": {
        "X-API-Key": "${sampleKey}"
      }
    }
  }
}`} />

        <h3 className="dev-snippet-lang">Cursor</h3>
        <p className="dev-muted" style={{ fontStyle: 'normal', marginBottom: 8 }}>
          Add to <code>.cursor/mcp.json</code> in your project root:
        </p>
        <CodeBlock id="mcp-cursor" code={`{
  "mcpServers": {
    "xrank": {
      "url": "https://rankify-api.aramailabs.com/mcp",
      "type": "streamable-http",
      "headers": {
        "X-API-Key": "${sampleKey}"
      }
    }
  }
}`} />

        <h3 className="dev-snippet-lang">Windsurf</h3>
        <p className="dev-muted" style={{ fontStyle: 'normal', marginBottom: 8 }}>
          Add to <code>~/.codeium/windsurf/mcp_config.json</code>:
        </p>
        <CodeBlock id="mcp-windsurf" code={`{
  "mcpServers": {
    "xrank": {
      "url": "https://rankify-api.aramailabs.com/mcp",
      "serverType": "sse",
      "headers": {
        "X-API-Key": "${sampleKey}"
      }
    }
  }
}`} />
      </section>
    </>
  );

  return (
    <div className="upload-page">
      <main className="upload-main">
        <div className="upload-container" style={{ maxWidth: 960 }}>
          <div className="upload-form">
            <h1 className="dev-page-title">Developer</h1>
            <p className="dev-page-subtitle">API keys, usage, REST integration, and MCP configuration.</p>

            {error && (
              <div className="dev-error-banner">
                <span>⚠️ {error}</span>
                <button onClick={() => setError(null)} className="dev-error-dismiss">✕</button>
              </div>
            )}

            {/* Tab bar */}
            <div className="dev-tab-bar">
              {TAB_ITEMS.map((t) => (
                <button
                  key={t.id}
                  className={`dev-tab-btn${activeTab === t.id ? ' active' : ''}`}
                  onClick={() => setActiveTab(t.id)}
                >
                  <span className="dev-tab-icon">{t.icon}</span> {t.label}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div className="dev-tab-content">
              {activeTab === 'keys' && renderKeysTab()}
              {activeTab === 'rest' && renderRestTab()}
              {activeTab === 'mcp' && renderMcpTab()}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(0)} MB`;
}
